import _ from 'underscore'
import React from 'react'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu as UIDropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem as UIDropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

// Legacy bootstrap tokens mapped onto the shadcn cva variants/sizes (same
// approach as button.js). Any unmapped classes pass through.
const VARIANTS = {
  'btn-primary': 'default',
  'btn-success': 'default',
  'btn-danger': 'destructive',
  'btn-default': 'outline',
  'btn-outline': 'outline',
  'btn-secondary': 'outline',
  'btn-link': 'link',
}

const SIZES = {
  'btn-sm': 'sm',
  'btn-xs': 'sm',
  'btn-lg': 'lg',
}

// Item values are either a scalar or an object keyed by id; normalize so the
// React key and the selected-comparison agree.
const valueKey = (value) => (value && value.id != null) ? value.id : value

class DropdownMenu extends React.Component {

  constructor(props) {
    super(props)

    this.state = {
      search: ''
    }

    this.searchRef = React.createRef()
  }

  onOpenChange = (open) => {
    if (open && this.props.searchable) {
      this.setState({search: ''})
    }
  }

  onOpenAutoFocus = (e) => {
    // Keep Radix from focusing the menu so the search box gets focus instead.
    if (this.props.searchable && this.searchRef.current) {
      e.preventDefault()
      this.searchRef.current.focus()
    }
  }

  onItemClick = (value) => {
    this.props.onItemClick(value)
  }

  onSearchChange = (e) => {
    this.setState({search: e.target.value})
  }

  onSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      const content = e.currentTarget.closest('[data-slot="dropdown-menu-content"]')
      const item = content && content.querySelector('[data-slot="dropdown-menu-item"]')
      if (item) {
        item.focus()
      }
    }
    // Keep typing in the search box from triggering Radix's item typeahead,
    // but let Escape through so the menu still closes.
    if (e.key !== 'Escape') {
      e.stopPropagation()
    }
  }

  triggerVariantProps = () => {
    let variant = null
    let size = null
    const passthrough = []
    const buttonClassName = this.props.buttonClassName || 'btn-outline'

    buttonClassName.split(/\s+/).forEach((token) => {
      if (VARIANTS[token]) {
        variant = variant || VARIANTS[token]
      }
      else if (SIZES[token]) {
        size = size || SIZES[token]
      }
      else if (token && token !== 'btn') {
        passthrough.push(token)
      }
    })

    return {
      variant: variant || 'outline',
      size: size || 'default',
      className: cn(passthrough)
    }
  }

  render() {
    let items = this.props.items
    const search = this.state.search.toLowerCase()
    const align = (this.props.className || '').includes('dropdown-menu-right') ? 'end' : 'start'

    if (this.state.search.length > 0) {
      items = _.select(items, (item) => {
        return item.name.toLowerCase().includes(search)
      })
    }

    return (
      <UIDropdownMenu onOpenChange={this.onOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button type="button" disabled={this.props.disabled} {...this.triggerVariantProps()}>
            {this.props.children || this.props.name}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={align}
          // Radix measures the space to the viewport edge; without a scroll
          // container long lists (e.g. assignees) ran off the bottom of the
          // screen. Search stays pinned, items scroll.
          // Width: fit the items (long template names stay on one line)
          // rather than the trigger button, which is only a floor.
          className="flex max-h-(--radix-dropdown-menu-content-available-height) w-auto min-w-(--radix-dropdown-menu-trigger-width) max-w-[480px] flex-col overflow-hidden"
          onOpenAutoFocus={this.onOpenAutoFocus}
        >
          {this.props.searchable ?
            <Input
              ref={this.searchRef}
              placeholder="Search"
              className="mb-1.5"
              value={this.state.search}
              onChange={this.onSearchChange}
              onKeyDown={this.onSearchKeyDown}
            />
            : null
          }
          {/* Bleed over the content's right padding so the scrollbar sits at
            * the menu edge instead of 6px inside it. */}
          <div className="-mr-1.5 min-h-0 flex-1 overflow-y-auto pr-1.5">
            {_.map(items, (item) => {
              return (
                <DropdownMenuItem
                  key={valueKey(item.value)}
                  name={item.name}
                  content={item.content}
                  count={item.count}
                  value={item.value}
                  selected={this.props.selected != null && `${valueKey(item.value)}` === `${this.props.selected}`}
                  onClick={this.onItemClick}
                />
              )
            })}
          </div>
        </DropdownMenuContent>
      </UIDropdownMenu>
    )
  }
}

class DropdownMenuItem extends React.Component {

  onSelect = () => {
    this.props.onClick(this.props.value)
  }

  render() {
    return (
      <UIDropdownMenuItem onSelect={this.onSelect}>
        {this.props.content || this.props.name}
        {this.props.count ? <span className="text-muted-foreground">({this.props.count})</span> : null}
        {this.props.selected ? <Check className="ml-auto size-4"/> : null}
      </UIDropdownMenuItem>
    )
  }
}

export { DropdownMenu, DropdownMenuItem }
